import { useQuery } from '@tanstack/react-query';
import type {
  DraftUsageMetrics,
  MetricsOverview,
  ResponseTimeMetrics,
} from '@anchor-comms/shared';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function MetricsPage() {
  const overview = useQuery({
    queryKey: ['metrics', 'overview'],
    queryFn: () => api<MetricsOverview>('/api/v1/metrics/overview'),
  });
  const draftUsage = useQuery({
    queryKey: ['metrics', 'draft-usage'],
    queryFn: () => api<DraftUsageMetrics>('/api/v1/metrics/draft-usage'),
  });
  const responseTime = useQuery({
    queryKey: ['metrics', 'response-time'],
    queryFn: () => api<ResponseTimeMetrics>('/api/v1/metrics/response-time'),
  });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Metrics</h1>
      <p className="text-sm text-muted-foreground">
        Baseline metrics. Definitions for &quot;response time&quot; and per-topic outcomes will be refined
        with the manager after the demo.
      </p>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Total conversations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold">{overview.data?.totalConversations ?? '—'}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Draft usage</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <div>Total drafts: {draftUsage.data?.totalDrafts ?? '—'}</div>
            <div>Used as-is: {draftUsage.data?.usedAsIs ?? '—'}</div>
            <div>Used with edits: {draftUsage.data?.usedWithEdits ?? '—'}</div>
            <div>Ignored: {draftUsage.data?.ignored ?? '—'}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Avg response time</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <div className="text-3xl font-semibold">
              {responseTime.data?.averageSeconds
                ? `${Math.round(responseTime.data.averageSeconds / 60)}m`
                : '—'}
            </div>
            <p className="text-muted-foreground">
              over {responseTime.data?.sampleSize ?? 0} customer→rep pairs
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>By topic</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {overview.data?.byTopic.map((t) => (
                <li key={t.topicId} className="flex justify-between">
                  <span>{t.topicName}</span>
                  <span className="font-medium">{t.count}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>By outcome</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {overview.data?.byOutcome.map((o, i) => (
                <li key={i} className="flex justify-between">
                  <span>{o.outcome ?? '(none)'}</span>
                  <span className="font-medium">{o.count}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>By rep</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {overview.data?.byRep.map((r) => (
                <li key={r.repId} className="flex justify-between">
                  <span>{r.repName}</span>
                  <span className="font-medium">{r.count}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
